// src/components/blog/features/BlogEmailCapture.tsx

/**
 * BlogEmailCapture Component
 * 
 * A lazy-loaded email capture form optimized for blog pages with intersection observer
 * for performance. Renders a skeleton loader until the form is visible in the viewport.
 * 
 * Features:
 * - Lazy loading with React.lazy and Suspense
 * - Intersection Observer for viewport-based loading
 * - Skeleton loader for better perceived performance
 * - Configurable email template and validation
 * - CRM integration with tags and source tracking
 * - Analytics tracking for newsletter signups
 * 
 * @example
 * ```tsx
 * <BlogEmailCapture className="my-8" />
 * ```
 */

'use client'

import { Suspense, lazy, useEffect, useRef, useState } from 'react'

import { IconComponent } from '~/components/shared/IconComponent'
import { cn } from '~/lib/utils'

/**
 * Lazy-loaded email capture form component
 * Dynamically imports BaseEmailCaptureForm to reduce initial bundle size
 */
const LazyEmailCaptureForm = lazy(async () => {
  const mod = await import('~/components/forms/base/BaseEmailCaptureForm')
  return { default: mod.BaseEmailCaptureForm }
})

/**
 * Props for the BlogEmailCapture component
 * 
 * @interface BlogEmailCaptureProps
 * @property {string} [className] - Optional CSS class name for styling the container
 */
interface BlogEmailCaptureProps {
  /** Optional CSS class name for styling the container */
  className?: string
}

/**
 * Props for the EmailFormSkeleton component
 * 
 * @interface EmailFormSkeletonProps
 * @property {string} [className] - Optional CSS class name for styling the skeleton
 */
interface EmailFormSkeletonProps {
  /** Optional CSS class name for styling the skeleton */
  className?: string
}

/**
 * Skeleton loader component displayed while the email form is loading
 * Matches the visual structure of the actual form for smooth transitions
 */
function EmailFormSkeleton({ className }: EmailFormSkeletonProps): JSX.Element {
  return (
    <div className={cn('relative mx-auto max-w-xl', className)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-0">
        <div className="h-12 w-full rounded-xl bg-white/10 sm:rounded-r-none" />
        <div className="h-12 w-36 rounded-xl bg-white/20 sm:rounded-l-none" />
      </div>
      <div className="mx-auto mt-3 h-4 w-52 rounded bg-white/10" />
    </div>
  )
}

/**
 * Blog email capture component with lazy loading and intersection observer
 * 
 * This component uses intersection observer to detect when it enters the viewport,
 * then lazy loads the actual form component. This improves initial page load performance
 * by deferring the loading of the form until it's needed.
 */
export function BlogEmailCapture({ className }: BlogEmailCaptureProps): JSX.Element {
  const [shouldRenderForm, setShouldRenderForm] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Skip if form is already rendered
    if (shouldRenderForm) {
      return
    }

    const element = containerRef.current
    if (!element) {
      return
    }

    // Fallback for environments without IntersectionObserver support
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- setState runs in the IntersectionObserver callback, not synchronously in the effect
      setShouldRenderForm(true)
      return
    }

    // Set up intersection observer to load form when it enters viewport
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setShouldRenderForm(true)
            observer.disconnect()
          }
        })
      },
      { rootMargin: '200px 0px' } // Start loading 200px before entering viewport
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [shouldRenderForm])

  return (
    <div ref={containerRef}>
      <Suspense fallback={<EmailFormSkeleton className={className} />}>
        {shouldRenderForm ? (
          <LazyEmailCaptureForm
            id="blog-newsletter-form"
            name="blog-newsletter"
            className={className}
            template={{
              templateAlias: 'welcome',
              // templateAlias: 'blog-newsletter',
              to: '', // Will be set by form component
              from: process.env.NEXT_PUBLIC_EMAIL_SENDER ?? 'hello@adsights.ai',
              templateModel: {
                base: {
                  logo_url: process.env.NEXT_PUBLIC_LOGO_URL ?? 'https://www.adsights.ai/logo-square.png',
                  product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? 'AdSights',
                  product_url: process.env.NEXT_PUBLIC_PRODUCT_URL ?? 'https://www.adsights.ai',
                  support_email: process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? 'support@adsights.ai',
                  sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? 'The AdSights Team',
                  company_name: process.env.NEXT_PUBLIC_COMPANY_NAME ?? 'AdSights Inc.',
                  company_address:
                    process.env.NEXT_PUBLIC_COMPANY_ADDRESS ??
                    '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702'
                },
                custom: {
                  subject: 'Welcome to AdSights Insights 🎯',
                  preview_text: 'Thank you for subscribing to our newsletter!'
                }
              }
            }}
            validation={{
              rules: {
                requireCorporateEmail: false
              },
              messages: {
                required: 'Please enter your email address',
                invalid: 'Please enter a valid email address',
                corporate: 'Please use your corporate email address'
              }
            }}
            crm={{
              listId: 'blog-subscribers',
              tags: ['blog', 'newsletter'],
              source: 'blog'
            }}
            analytics={{
              category: 'Blog',
              action: 'Newsletter Signup',
              label: 'Blog Newsletter Form'
            }}
            aria={{
              label: 'Blog newsletter signup form',
              describedBy: 'blog-newsletter-description'
            }}
            content={{
              button: {
                text: 'Subscribe',
                loadingText: 'Subscribing...'
              },
              messages: {
                success: 'Thank you for subscribing!',
                error: 'Something went wrong. Please try again.'
              }
            }}
          >
            {({ state, handlers }) => (
              <div className="relative mx-auto max-w-xl">
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-0">
                  <div className="relative flex-1">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className={cn(
                        'block w-full rounded-xl border-0 bg-white/10 px-4 py-3.5 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 sm:rounded-r-none',
                        'text-base transition duration-200',
                        'hover:bg-white/20',
                        state.error && 'ring-2 ring-red-500'
                      )}
                      disabled={state.isSubmitting}
                      onChange={handlers.handleChange}
                      onBlur={handlers.handleBlur}
                    />
                    {state.error && <p className="mt-2 text-sm text-red-400">{state.error}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={state.isSubmitting || !state.email}
                    className={cn(
                      'relative flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold transition duration-200 sm:rounded-l-none',
                      'bg-blue-600 text-white hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      state.isSubmitting && 'cursor-wait',
                      state.isSuccess && 'bg-green-600 hover:bg-green-500'
                    )}
                    onClick={handlers.handleSubmit}
                  >
                    {state.isSubmitting ? (
                      <>
                        <IconComponent name="RiLoader4Line" className="h-5 w-5 animate-spin" />
                        <span>Subscribing...</span>
                      </>
                    ) : state.isSuccess ? (
                      <>
                        <IconComponent name="RiCheckLine" className="h-5 w-5" />
                        <span>Subscribed!</span>
                      </>
                    ) : (
                      <>
                        <IconComponent name="RiMailLine" className="h-5 w-5" />
                        <span>Subscribe</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="mt-3 text-center text-sm text-gray-400">
                  We respect your privacy. Unsubscribe at any time.
                </div>
              </div>
            )}
          </LazyEmailCaptureForm>
        ) : (
          <EmailFormSkeleton className={className} />
        )}
      </Suspense>
    </div>
  )
}